/**
 * @class AcademyTextReveal
 * Section-agnostic line-by-line text reveal: splits the wrapped heading(s)
 * into lines (GSAP SplitText), then sweeps an opaque color block across
 * each line — the line's text only becomes visible once the block has
 * fully covered and is sweeping back off it. Vanilla-JS port of the
 * user-supplied React "Copy" component reference (SplitText + a
 * `.block-line-wrapper`/`.block-revealer` per line, built paused up
 * front and played on trigger). Screen-reader handling (a plain,
 * visually-hidden clone of the original content, with the animated/split
 * version hidden from assistive tech via aria-hidden) is ported from a
 * separate, proven production reference (landonorris.com).
 *
 * Usable in any section by wrapping the target heading(s):
 *
 *   <academy-text-reveal data-color="#042B48">
 *     <h2>Heading</h2>
 *   </academy-text-reveal>
 *
 * Attributes (all optional):
 *   data-color     block color, default #000
 *   data-stagger   seconds between each line's start, default 0.15
 *   data-duration  seconds per sweep (in and out), default 0.75
 *   data-delay     seconds before the first line starts, default 0
 *   data-start     GSAP ScrollTrigger `start` string for the scroll-in
 *                  trigger point, default "top 78%" (e.g. "top 95%" to
 *                  fire earlier)
 *   data-scroll    "false" to play immediately instead of on scroll-into-view,
 *                  "manual" to set up the split but wait for an external
 *                  call to el.playReveal() (e.g. an accordion open event)
 *   data-multi     present when this element wraps MULTIPLE headings that
 *                  should each be split individually (default: split the
 *                  element itself as a single block)
 *   data-resplit   present to re-run the line split on viewport resize
 *                  (recomputes line breaks + wrapper widths). Opt-in:
 *                  safe only when this element has a stable layout width
 *                  at all times — NOT for content in absolutely
 *                  positioned / transformed / temporarily hidden boxes,
 *                  which mis-measure mid-animation and collapse to one
 *                  word per line.
 *
 * Dispatches a `academytextreveal:play` CustomEvent (bubbles) the moment
 * the reveal starts, so sibling components can sequence off it (e.g. an
 * image reveal that should start slightly after the text).
 *
 * Cleans itself up on disconnect (kills timelines/ScrollTrigger, unwraps
 * the line wrappers, reverts the SplitText split, removes the
 * screen-reader clone) — the Shopify theme editor re-renders a section's
 * HTML on essentially every settings change, tearing down and recreating
 * this element each time.
 */
if (!customElements.get('academy-text-reveal')) {
  class AcademyTextReveal extends HTMLElement {
    connectedCallback() {
      // Libraries that reparent elements as part of their own layout
      // machinery (notably GSAP ScrollTrigger's pin-spacer: pinning, and
      // every ScrollTrigger.refresh() after, briefly swaps the pinned
      // subtree out of and back into the DOM synchronously) trigger a
      // disconnect immediately followed by a reconnect in the same tick.
      // A pending teardown from disconnectedCallback below means exactly
      // that just happened — cancel it and keep our existing split/
      // timelines intact instead of re-initializing from scratch.
      if (this._pendingDisconnect) {
        clearTimeout(this._pendingDisconnect);
        this._pendingDisconnect = null;
      }

      if (this._inited) return;
      if (typeof gsap === 'undefined' || typeof SplitText === 'undefined') return;
      this._inited = true;
      this._splits = [];
      this._timelines = [];
      this._trigger = null;
      this._srClone = null;
      this.run();
    }

    disconnectedCallback() {
      // Deferred by a tick (see connectedCallback above): a same-tick
      // reconnect (GSAP pin-spacer swap) cancels this before it runs, so
      // real teardown only happens for an actual, lasting removal —
      // e.g. the Shopify theme editor re-rendering a section. Running
      // teardown synchronously here used to re-run SplitText on every
      // transient pin swap, which itself reparents nodes and re-fires
      // this same callback — an unbounded connectedCallback/
      // disconnectedCallback recursion that crashed the page with
      // "Maximum call stack size exceeded" and took every other GSAP
      // animation on the page down with it.
      this._pendingDisconnect = setTimeout(() => {
        this._pendingDisconnect = null;
        this._teardown();
      }, 0);
    }

    _teardown() {
      this._inited = false;

      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      clearTimeout(this._resizeDebounce);
      clearTimeout(this._resplitRetry);

      if (this._trigger) {
        this._trigger.kill();
        this._trigger = null;
      }

      this._timelines.forEach((tl) => tl.kill());
      this._timelines = [];

      // Unwrap: move each line back out of its wrapper and remove the
      // wrapper — SplitText's own revert() doesn't know about these
      // wrapper/block divs since we created them ourselves.
      this.querySelectorAll('.academy-text-reveal__line-wrapper').forEach((wrapper) => {
        if (wrapper.parentNode && wrapper.firstChild) {
          wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
        }
        wrapper.remove();
      });

      // The <span> we wrap each <li>'s own content in (see revealList) has
      // no SplitText split to revert it, so flatten it here — otherwise a
      // theme-editor re-render would nest another span inside it each time.
      this.querySelectorAll('span.academy-text-reveal__line').forEach((span) => {
        while (span.firstChild) {
          span.parentNode.insertBefore(span.firstChild, span);
        }
        span.remove();
      });

      this._splits.forEach((split) => {
        try {
          split.revert();
        } catch (e) {
          // no-op — element may already be gone
        }
      });
      this._splits = [];

      if (this._srClone) {
        this._srClone.remove();
        this._srClone = null;
      }
      this.removeAttribute('aria-hidden');
    }

    // Viewport resize: SplitText froze the line breaks at the width the
    // element had when it first split, and each line wrapper is sized to
    // that line's text (width: max-content) — so a resize leaves the
    // breaks, the wrapper widths and the sweep blocks all measured for
    // the old width. Tear the split down and rebuild it against the
    // current width. If the reveal had already played, rebuild straight
    // into the finished (revealed) state instead of re-arming the
    // scroll trigger, so the text doesn't disappear on resize.
    _resplit() {
      if (!this._inited) return;
      // Never yank the DOM out from under a running sweep — retry once
      // it has settled.
      if (this._timelines.some((tl) => tl.isActive())) {
        clearTimeout(this._resplitRetry);
        this._resplitRetry = setTimeout(() => this._resplit(), 150);
        return;
      }

      const wasRevealed = !!this._hasPlayed;
      // Carry the SR clone (identical text at any width) and the resize
      // listener itself across the rebuild — _teardown() would otherwise
      // drop both.
      const keepClone = this._srClone;
      const keepResize = this._onResize;
      this._srClone = null;
      this._onResize = null;
      this._teardown();
      this._srClone = keepClone;
      this._onResize = keepResize;

      this._inited = true;
      this._splits = [];
      this._timelines = [];
      this._trigger = null;
      this._forceRevealed = wasRevealed;
      this.run();
      this._forceRevealed = false;
    }

    run() {
      const color = this.dataset.color || '#000';
      const stagger = parseFloat(this.dataset.stagger) || 0.15;
      const duration = parseFloat(this.dataset.duration) || 0.75;
      const delay = parseFloat(this.dataset.delay) || 0;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollMode = this.dataset.scroll;
      const isManual = scrollMode === 'manual';
      const animateOnScroll = !isManual && scrollMode !== 'false';

      // SplitText fragments the text across many line elements, which some
      // screen readers announce word-by-word, skip, or read out of order.
      // Clone the original (un-split) content into a plain, visually
      // hidden duplicate for assistive tech, then hide the animated
      // version from it entirely.
      if (!this._srClone) {
        // Must be a plain <div>, NOT this.cloneNode(true): cloning the
        // custom-element host produces another upgraded <academy-text-reveal>,
        // whose own connectedCallback → run() clones itself again and again —
        // an infinite recursion that crashed with "Maximum call stack size
        // exceeded". A neutral wrapper carrying just the inner content is all
        // assistive tech needs.
        const clone = document.createElement('div');
        clone.className = 'visually-hidden';
        clone.innerHTML = this.innerHTML;
        this.parentNode.insertBefore(clone, this);
        this._srClone = clone;
      }
      this.setAttribute('aria-hidden', 'true');

      const roots = this.hasAttribute('data-multi') ? Array.from(this.children) : [this];
      const lines = [];
      const blocks = [];

      // Wrap one already-isolated line element in its sweep wrapper + block.
      // `flow` = the wrapper must wrap naturally in its column instead of
      // shrinking to its longest line (used for list items, see revealList).
      const wrapLine = (line, flow) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'academy-text-reveal__line-wrapper';
        if (flow) wrapper.classList.add('academy-text-reveal__line-wrapper--flow');
        line.parentNode.insertBefore(wrapper, line);
        wrapper.appendChild(line);
        line.classList.add('academy-text-reveal__line');

        const block = document.createElement('div');
        block.className = 'academy-text-reveal__block';
        block.style.backgroundColor = color;
        wrapper.appendChild(block);

        lines.push(line);
        blocks.push(block);
      };

      // Run SplitText on a single text-bearing element, wrap each line.
      const splitElement = (element) => {
        const split = SplitText.create(element, {
          type: 'lines',
          linesClass: 'academy-text-reveal__original-line++',
          lineThreshold: 0.1,
        });
        this._splits.push(split);
        split.lines.forEach((line) => wrapLine(line));
      };

      // Lists must NOT be handed to SplitText: splitting across a <ul>/<ol>
      // detaches every visual line into its own block, so a list item that
      // wrapped to two lines rendered one bullet PER LINE and the wrapping
      // fell apart on narrow screens (the same block-level-children failure
      // the <p>-in-<h2> note above describes). Instead sweep each real <li>
      // as one block, wrapping its CONTENTS (never the <li> itself, so its
      // marker is left intact) and recursing into nested lists.
      const revealList = (listEl) => {
        Array.from(listEl.children).forEach((li) => {
          if (li.tagName !== 'LI') return;

          const childLists = Array.from(li.children).filter(
            (c) => c.tagName === 'UL' || c.tagName === 'OL'
          );
          const firstList = childLists[0] || null;

          // Everything before the first nested list = this item's own text.
          const own = document.createElement('span');
          while (li.firstChild && li.firstChild !== firstList) {
            own.appendChild(li.firstChild);
          }
          if (own.childNodes.length) {
            li.insertBefore(own, firstList);
            wrapLine(own, true);
          }

          childLists.forEach((nested) => revealList(nested));
        });
      };

      roots.forEach((element) => {
        if (element.tagName === 'UL' || element.tagName === 'OL') {
          revealList(element);
        } else {
          splitElement(element);
        }
      });

      if (!lines.length) return;

      // Recompute the split (line breaks + wrapper widths) on viewport
      // resize — see _resplit(). OPT-IN via data-resplit only: elements
      // that live in absolutely-positioned / transformed / temporarily
      // hidden containers (e.g. the parallax-scroll image captions)
      // mis-measure when re-split mid-animation, collapsing to one word
      // per line. Registered once; carried across rebuilds and removed
      // in _teardown().
      if (this.hasAttribute('data-resplit') && !this._onResize) {
        this._onResize = () => {
          clearTimeout(this._resizeDebounce);
          this._resizeDebounce = setTimeout(() => this._resplit(), 150);
        };
        window.addEventListener('resize', this._onResize);
      }

      // Marks each line as shown — consumers can hook decorations (e.g. the
      // USP accordion's list bullets) onto `.academy-text-reveal__line.is-revealed`.
      const markRevealed = (line) => line && line.classList.add('is-revealed');

      if (reduceMotion) {
        blocks.forEach((block) => block.remove());
        lines.forEach(markRevealed);
        return;
      }

      gsap.set(lines, { opacity: 0 });
      gsap.set(blocks, { scaleX: 0, transformOrigin: 'left center' });

      // Rebuilt after a resize when the reveal had already finished:
      // show the fresh (re-wrapped) lines outright, no timeline, no
      // trigger — the sweep animation only plays on the first reveal.
      if (this._forceRevealed) {
        gsap.set(lines, { opacity: 1 });
        gsap.set(blocks, { scaleX: 0 });
        lines.forEach(markRevealed);
        this._hasPlayed = true;
        return;
      }

      // Built paused, up front, for every line, then played on demand
      // (scroll-enter, manual call, or immediately) — matches the
      // reference exactly.
      // will-change scoped to the sweep itself via GSAP (not permanent CSS):
      // a block sits in the DOM for the page's whole lifetime after its one
      // sweep, so a CSS `will-change: transform` on it pins a compositor
      // layer forever for no visual benefit — same class of issue as the
      // pinned sections gated in academy-gpu-layer-gate.js, just per-line
      // instead of per-section.
      const timelines = blocks.map((block, index) => {
        const tl = gsap.timeline({ delay: delay + index * stagger, paused: true });
        tl.set(block, { willChange: 'transform' });
        tl.to(block, { scaleX: 1, duration, ease: 'power4.inOut' });
        tl.set(lines[index], { opacity: 1 });
        tl.call(markRevealed, [lines[index]]);
        tl.set(block, { transformOrigin: 'right center' });
        tl.to(block, { scaleX: 0, duration, ease: 'power4.inOut' });
        tl.set(block, { willChange: 'auto' });
        return tl;
      });
      this._timelines = timelines;

      const play = () => {
        this._hasPlayed = true;
        this.dispatchEvent(new CustomEvent('academytextreveal:play', { bubbles: true }));
        timelines.forEach((tl) => tl.play());
      };

      if (isManual) {
        this._played = false;
        this.playReveal = () => {
          if (this._played) return;
          this._played = true;
          play();
        };
      } else if (animateOnScroll && typeof ScrollTrigger !== 'undefined') {
        this._trigger = ScrollTrigger.create({
          trigger: this,
          // data-start overrides the scroll-in trigger point (any GSAP
          // ScrollTrigger `start` string, e.g. "top 95%"); defaults to
          // "top 78%".
          start: this.dataset.start || 'top 78%',
          once: true,
          onEnter: play,
        });
      } else {
        play();
      }
    }
  }

  customElements.define('academy-text-reveal', AcademyTextReveal);
}
